import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  forwardRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { ICascadingConfig } from '../../models/irembo-cascading-config';

@Component({
  selector: 'irembogov-cascading-drop-downs',
  templateUrl: './irembo-cascading-drop-downs.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
  styleUrls: ['./irembo-cascading-drop-downs.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IremboCascadingDropDownsComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: IremboCascadingDropDownsComponent,
      multi: true,
    },
  ],
})
export class IremboCascadingDropDownsComponent
  implements ControlValueAccessor, Validator, OnInit, OnChanges
{
  @Input() loading: Record<string, boolean> = {};
  @Input() configs: ICascadingConfig[] = [];
  @Input() items: Record<string, Record<string, unknown>[] | null> | undefined;
  @Input() isTouched = false;
  @Input() linear = false;
  @Input() model: any = {}; // Model context for expression evaluation
  @Input() field: any; // Field context for expression evaluation
  @Output() activeDropDown = new EventEmitter<ICascadingConfig>();

  form: FormGroup | undefined;

  constructor(private readonly cd: ChangeDetectorRef) {}

  ngOnInit(): void {
    const group: Record<string, FormControl> = this.buildFormFields(
      this.configs
    );
    this.form = new FormGroup(group);
    this.activeDropDown.emit(this.configs[0]);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isTouched'] && this.isTouched && this.form) {
      this.form.markAllAsTouched();
    }

    // When the configs themselves change, update validators
    if (changes['configs']) {
      this.updateValidators();
      try {
        const previousConfigs = changes['configs'].previousValue;
        const currentConfigs = changes['configs'].currentValue;
        if (Array.isArray(previousConfigs) && Array.isArray(currentConfigs)) {
          if (
            previousConfigs.length !== currentConfigs.length ||
            JSON.stringify(previousConfigs) !== JSON.stringify(currentConfigs)
          ) {
            const group: Record<string, FormControl> =
              this.buildFormFields(currentConfigs);
            this.form = new FormGroup(group);
            this.activeDropDown.emit(changes['configs'].currentValue[0]);
          }
        }
      } catch (error) {
        console.error('Error updating configs:', error);
      }
    }

    // When the model changes, re-evaluate the expressions
    if (changes['model']) {
      this.updateValidators();
    }
  }

  /**
   * Re-evaluates expressions for each control and updates its validators accordingly.
   */
  updateValidators(): void {
    if (!this.form) return;

    this.configs.forEach(config => {
      const control = this.form?.get(config.key);
      if (control) {
        const isRequired = this.evaluateExpression(
          config,
          'required',
          config.required
        );

        // Update validators based on the evaluated expression
        if (isRequired) {
          control.setValidators(Validators.required);
        } else {
          control.clearValidators();
        }

        // Update validity without emitting value changes
        control.updateValueAndValidity({ emitEvent: false });
      }
    });

    // Trigger change detection to update the UI (especially the asterisks)
    // Use setTimeout to ensure Angular's change detection cycle completes
    setTimeout(() => {
      this.cd.detectChanges();
    }, 0);
  }

  evaluateExpression(
    config: ICascadingConfig,
    property: string,
    defaultValue: any
  ): any {
    if (!(config.expressions || config?.expressions?.[property])) {
      return defaultValue;
    }
    try {
      const expression = config.expressions[property];

      // Create evaluation context with both model and field
      const func = new Function(
        'model',
        'field',
        'config',
        `
          try {
            return ${expression};
          } catch (e) {
            console.error('Expression evaluation error:', e);
            return ${JSON.stringify(defaultValue)};
          }
        `
      );

      const result = func(this.model || {}, this.field || {}, config);
      return result;
    } catch (error) {
      console.error(
        `Error evaluating expression for ${config.key}.${property}:`,
        error
      );
      console.error('Expression:', config.expressions[property]);
      return defaultValue;
    }
  }

  // No changes needed for the rest of the methods...

  resetDropDowns(_: string, index: number) {
    this.configs.slice(index).forEach(child => {
      this.form?.controls[child.key].setValue(undefined);
    });
  }

  onSelectChange(
    value: Record<string, unknown> | Array<Record<string, unknown>>,
    key: string,
    index: number
  ) {
    const nextItemIndex = index + 1;
    this.resetDropDowns(key, nextItemIndex);
    this._onChange(this.form?.value);
    this._onTouch(this.form?.value);
    if (this.configs.length - 1 === index || !value) return;

    const emitValue = Array.isArray(value) ? value[0] : value;
    if (emitValue) {
      this.activeDropDown.emit({
        ...this.configs[nextItemIndex],
        value: emitValue,
      });
    }
  }

  private _onChange = (value: unknown) => {
    console.log('Value changed:', value);
  };
  private _onTouch = (value: unknown) => {
    console.log('Touched:', value);
  };

  writeValue(obj: { [key: string]: any }): void {
    if (obj && this.form) {
      // Use patchValue to avoid errors if obj doesn't contain all form keys
      this.form.patchValue(obj, { emitEvent: false });
    }

    if (obj === null) {
      this.form?.reset();
    }
  }

  registerOnChange(fn: (_: unknown) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: (_: unknown) => void): void {
    this._onTouch = fn;
  }

  validate(): ValidationErrors | null {
    return this.form?.invalid ? { invalid: true } : null;
  }

  private buildFormFields(configs: ICascadingConfig[]) {
    const group: Record<string, FormControl> = {};
    configs.forEach(config => {
      const isRequired = this.evaluateExpression(
        config,
        'required',
        config.required
      );
      group[config.key] = new FormControl(
        null,
        isRequired ? [Validators.required] : []
      );
    });
    return group;
  }
}
